import { Archive, MoreVert } from "@mui/icons-material";
import {
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router";
import {
  getApiServicesChatSessionsOptions,
  getApiServicesChatSessionsQueryKey,
  postApiServicesChatSessionsByIdArchiveMutation,
} from "@/api/generated/@tanstack/react-query.gen";
import { ConfirmationDialog } from "@/components/confirmation-dialog";

export function SessionList() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading } = useQuery({
    ...getApiServicesChatSessionsOptions({
      query: { archived: "false", limit: 50 },
    }),
  });

  const archiveMutation = useMutation({
    ...postApiServicesChatSessionsByIdArchiveMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiServicesChatSessionsQueryKey(),
      });
      const wasActive = menuTarget === sessionId;
      handleCloseConfirm();
      if (wasActive) {
        navigate("/");
      }
    },
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuTarget(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuTarget(null);
  };

  const handleArchiveClick = () => {
    setAnchorEl(null);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setMenuTarget(null);
  };

  const handleConfirmArchive = () => {
    if (!menuTarget) return;
    archiveMutation.mutate({ path: { id: menuTarget } });
  };

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        Loading...
      </Typography>
    );
  }

  if (!data?.items.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        No chats yet
      </Typography>
    );
  }

  return (
    <>
      <List disablePadding dense>
        {data.items.map((session) => (
          <ListItemButton
            key={session.id}
            component={NavLink}
            to={`/chat/${session.assistantId}/${session.id}`}
            sx={{
              borderRadius: 1,
              "&.active": {
                bgcolor: "primary.light",
                color: "primary.main",
              },
              "& .session-menu-btn": {
                opacity: 0,
              },
              "&:hover .session-menu-btn, & .session-menu-btn.menu-open": {
                opacity: 1,
              },
            }}
          >
            <ListItemText
              primary={session.title ?? "Untitled chat"}
              primaryTypographyProps={{ noWrap: true, variant: "body2" }}
            />
            <IconButton
              size="small"
              className={`session-menu-btn${anchorEl && menuTarget === session.id ? " menu-open" : ""}`}
              onClick={(e) => handleOpenMenu(e, session.id)}
              edge="end"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </ListItemButton>
        ))}
      </List>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleArchiveClick}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          Archive
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        open={confirmOpen}
        title="Archive chat"
        message="Are you sure you want to archive this chat session?"
        confirmLabel="Archive"
        isLoading={archiveMutation.isPending}
        onConfirm={handleConfirmArchive}
        onCancel={handleCloseConfirm}
      />
    </>
  );
}
